import React, { useRef, useEffect, useState } from "react";
import { format } from "date-fns";
import styles from "../../components/Report/NotesPage.module.css";

export interface Note {
  _id: string;
  noteCategory: "clinical" | "non-clinical";
  richText: string;
  createdTimestamp: number;
  author?: string;
  isImportant?: "yes" | "no";
}

interface NotesPageProps {
  notes?: Note[];
  loading?: boolean;
  customHeight?: string;
  isPdf?: boolean;
  patientId?: string;
  startingIndex?: number;
  customWidth?: string;
  patientBillingMinuteMessage?: string;
}

interface SliceNote {
  note: Note;
  isSlice: boolean;
  showHeader: boolean;
  sliceHtml?: string;
}

interface NotePage {
  items: SliceNote[];
}

const NewNotesPage: React.FC<NotesPageProps> = ({
  notes = [],
  loading = false,
  customHeight = "10in",
  isPdf = false,
  customWidth,
  patientBillingMinuteMessage,
}) => {
  const PAGE_HEIGHT_PX = 10.3 * 96 - 110;
  const [pages, setPages] = useState<NotePage[]>([]);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!measureRef.current || loading) {
      setPages([]);
      return;
    }

    const hidden = measureRef.current;
    const nodes = Array.from(hidden.children) as HTMLElement[];

    const resultPages: NotePage[] = [];
    let currentPage: SliceNote[] = [];
    let currentHeight = 0;
    const subHeaderHeight = patientBillingMinuteMessage ? 48 : 0;
    let isFirstPage = true;

    notes.forEach((note, i) => {
      const node = nodes[i];

      const rect = node.getBoundingClientRect();
      const style = window.getComputedStyle(node);

      const fullHeight =
        rect.height +
        parseFloat(style.marginTop || "0") +
        parseFloat(style.marginBottom || "0");

      // FULL NOTE
      if (fullHeight <= PAGE_HEIGHT_PX) {
        const pageLimit = PAGE_HEIGHT_PX - (isFirstPage ? subHeaderHeight : 0);

        if (currentHeight + fullHeight > pageLimit) {
          resultPages.push({ items: [...currentPage] });
          currentPage = [];
          currentHeight = 0;
          isFirstPage = false;
        }

        currentPage.push({ note, isSlice: false, showHeader: true });
        currentHeight += fullHeight;
        return;
      }

      // big note
      const bodyClass = CSS.escape(styles.noteBody);
      const headerClass = CSS.escape(styles.noteHeader);

      const bodyEl = node.querySelector(`.${bodyClass}`) as HTMLElement;
      const headerEl = node.querySelector(`.${headerClass}`) as HTMLElement;

      const headerHeight = headerEl.getBoundingClientRect().height;
      const blocks = Array.from(bodyEl.children) as HTMLElement[];

      let blockIndex = 0;
      let firstSlice = true;

      while (blockIndex < blocks.length) {
        let sliceBlocks: HTMLElement[] = [];
        let sliceHeight = 0;

        while (blockIndex < blocks.length) {
          const el = blocks[blockIndex];
          const r = el.getBoundingClientRect();
          const cs = window.getComputedStyle(el);

          const h =
            r.height +
            parseFloat(cs.marginTop || "0") +
            parseFloat(cs.marginBottom || "0");

          const available = firstSlice
            ? PAGE_HEIGHT_PX -
              headerHeight -
              (isFirstPage ? subHeaderHeight : 0)
            : PAGE_HEIGHT_PX - (isFirstPage ? subHeaderHeight : 0);

          if (sliceHeight + h > available) break;

          sliceBlocks.push(el);
          sliceHeight += h;
          blockIndex++;
        }

        // CREATE PAGE SLICE
        if (currentHeight + sliceHeight > PAGE_HEIGHT_PX) {
          resultPages.push({ items: [...currentPage] });
          currentPage = [];
          currentHeight = 0;
          isFirstPage = false;
        }

        const temp = document.createElement("div");
        sliceBlocks.forEach((b) => temp.appendChild(b.cloneNode(true)));

        currentPage.push({
          note,
          isSlice: true,
          showHeader: firstSlice,
          sliceHtml: temp.innerHTML,
        });

        currentHeight += sliceHeight;
        firstSlice = false;
      }
    });

    if (currentPage.length > 0) {
      resultPages.push({ items: currentPage });
    }

    setPages(resultPages);
  }, [notes, loading]);

  if (loading) {
    return (
      <div
        className={styles.content}
        style={{ height: customHeight, width: customWidth }}
      >
        <div className={styles.pageContainer}>
          <div className={styles.contentInner}>
            <div className={styles.sectionHeader}>PATIENT NOTES</div>
            <div className={styles.loading}>Loading notes...</div>
          </div>
        </div>
      </div>
    );
  }
  return (
    <>
      {pages.map((page, pageIndex) => (
        <div
          key={pageIndex}
          className={styles.content}
          style={{
            height: "10.3in",
            maxHeight: "10.3in",
            pageBreakAfter: "always",
            overflow: "hidden",
            marginBottom: isPdf ? "0" : "1rem",
          }}
        >
          <div className={styles.sectionHeader}>PATIENT NOTES</div>
          {pageIndex === 0 && patientBillingMinuteMessage && (
            <div
              style={{
                fontSize: "1.4rem",
                fontWeight: 500,
                margin: "1rem 0",
                height: "3rem",
                backgroundColor: "#e5e7eb",
              }}
            >
              <div style={{ marginTop: "0.3rem", marginLeft: "1rem" }}>
                {patientBillingMinuteMessage}
              </div>
            </div>
          )}

          {page.items.length > 0 ? (
            page.items.map((item, idx) => (
              <div
                key={`${item.note._id}-${idx}`}
                className={styles.noteCard}
                style={
                  item.note.isImportant === "yes"
                    ? { border: "2px solid red" }
                    : {}
                }
              >
                {item.showHeader && (
                  <div className={styles.noteHeader}>
                    {item.note.noteCategory === "clinical"
                      ? "Clinical Note"
                      : "Internal Note"}
                    {" - "}
                    {item.note.createdTimestamp
                      ? format(
                          new Date(item.note.createdTimestamp),
                          "MM/dd/yyyy"
                        )
                      : "N/A"}
                  </div>
                )}

                <div
                  className={styles.noteBody}
                  dangerouslySetInnerHTML={{
                    __html: item.sliceHtml || item.note.richText,
                  }}
                />
              </div>
            ))
          ) : (
            <div className={styles.noData}>
              {loading
                ? "Loading notes..."
                : "No notes available for this billing cycle."}
            </div>
          )}
        </div>
      ))}

      {isPdf && <div style={{ height: "1in", visibility: "hidden" }} />}

      {/* hidden div */}
      <div
        ref={measureRef}
        style={{
          position: "absolute",
          visibility: "hidden",
          width: "8in",
          left: "-9999px",
          top: "-9999px",
        }}
      >
        {notes.map((note) => (
          <div
            key={`measure-${note._id}`}
            className={styles.noteCard}
            style={
              note.isImportant === "yes" ? { border: "2px solid red" } : {}
            }
          >
            <div className={styles.noteHeader}>
              {note.noteCategory === "clinical"
                ? "Clinical Note"
                : "Internal Note"}
              {" - "}
              {note.createdTimestamp
                ? format(new Date(note.createdTimestamp), "MM/dd/yyyy")
                : "N/A"}
            </div>
            <div
              className={styles.noteBody}
              dangerouslySetInnerHTML={{
                __html: note.richText,
              }}
            />
          </div>
        ))}
      </div>
    </>
  );
};

export default NewNotesPage;
